import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ApplicantState } from './types';
import {
  Applicant,
  ApplicantCreatePayloadRequest,
  ApplicantsCreateRequest,
  ApplicantsDestroyRequest
} from '../../../generated/api';
import { getApplicantApiClient } from '../../../common/apiClients';
import { RootState } from '../../../app/store';
import { getMessageFromError } from '../../../common/utils/apiUtils';

const sliceName = 'applicants';

const initialState: ApplicantState = {
  loading: false,
  applicants: []
};

export const fetchApplicantsAsync = createAsyncThunk<Applicant[]>(
  `${sliceName}/fetchApplicants`,
  async (arg, { rejectWithValue }) => {
    try {
      const apiClient = await getApplicantApiClient();
      return await apiClient.applicantsList();
    } catch (e) {
      return rejectWithValue(await getMessageFromError(e));
    }
  }
);

export const createApplicantAsync = createAsyncThunk<Applicant, ApplicantCreatePayloadRequest>(
  `${sliceName}/createApplicant`,
  async (arg, { rejectWithValue }) => {
    try {
      const apiClient = await getApplicantApiClient();
      const request: ApplicantsCreateRequest = { applicantCreatePayloadRequest: arg };
      return await apiClient.applicantsCreate(request);
    } catch (e) {
      return rejectWithValue(await getMessageFromError(e));
    }
  }
);

export const destroyApplicantAsync = createAsyncThunk<void, number>(
  `${sliceName}/destroyApplicant`,
  async (arg, { rejectWithValue, dispatch }) => {
    try {
      const apiClient = await getApplicantApiClient();
      const request: ApplicantsDestroyRequest = { id: arg };
      await apiClient.applicantsDestroy(request);
      dispatch(removeApplicant(arg));
    } catch (e) {
      return rejectWithValue(await getMessageFromError(e));
    }
  }
);

const applicantsSlice = createSlice({
  name: sliceName,
  initialState: initialState,
  reducers: {
    removeApplicant: (state: ApplicantState, action: PayloadAction<number>): ApplicantState => {
      return { ...state, applicants: state.applicants.filter((a) => a.id !== action.payload) };
    }
  },
  extraReducers: {
    [fetchApplicantsAsync.fulfilled.type]: (state, action: PayloadAction<Applicant[]>): ApplicantState => {
      return { ...state, loading: false, applicants: action.payload };
    },
    [createApplicantAsync.fulfilled.type]: (state, action: PayloadAction<Applicant>): ApplicantState => {
      return { ...state, loading: false, applicants: [action.payload, ...state.applicants] };
    }
  }
});

export const selectApplicants = (state: RootState): ApplicantState => state.applicants;
export const { removeApplicant } = applicantsSlice.actions;
export default applicantsSlice.reducer;
